/**
 * Detecta quando a aba está rodando um build antigo do front.
 *
 * Após um deploy, uma aba aberta continua com o JS do build anterior. Ao
 * navegar ou abrir um componente carregado sob demanda, o Next.js mistura
 * módulos dos dois builds e a falha nem sempre chega como `ChunkLoadError`
 * (ex.: "Cannot read properties of undefined (reading 'call')"), então o
 * `isChunkLoadError` não reconhece e o usuário vê a tela de erro.
 *
 * O `buildId` do Next vem no payload inicial da página (`self.__next_f`) e no
 * HTML servido. Se o do servidor for diferente do da aba, há versão nova e o
 * certo é recarregar em vez de mostrar erro.
 */

const BUILD_ID_REGEX = /buildId\\?"\s*:\s*\\?"([^"\\]+)/;

/** Extrai o buildId de um HTML ou payload RSC. */
export function extrairBuildId(texto: string | null | undefined): string | null {
    if (!texto) return null;
    const m = texto.match(BUILD_ID_REGEX);
    return m ? m[1] : null;
}

/** buildId com que a aba atual foi carregada. */
export function buildIdAtual(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        const flight = (window as any).__next_f;
        if (Array.isArray(flight)) {
            const id = extrairBuildId(flight.map((chunk: any) => String(chunk?.[1] ?? '')).join(''));
            if (id) return id;
        }
        return extrairBuildId(document.documentElement.innerHTML);
    } catch {
        return null;
    }
}

/**
 * true quando o servidor já serve um build diferente do da aba. Em qualquer
 * falha (offline, timeout, buildId ausente) responde false: na dúvida, a tela
 * de erro normal continua aparecendo.
 */
export async function existeVersaoNova(timeoutMs = 2500): Promise<boolean> {
    if (typeof window === 'undefined' || typeof fetch === 'undefined') return false;
    const atual = buildIdAtual();
    if (!atual) return false;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        const resp = await fetch(window.location.pathname, {
            cache: 'no-store',
            credentials: 'same-origin',
            signal: controller?.signal,
        });
        if (!resp.ok) return false;
        const servidor = extrairBuildId(await resp.text());
        return !!servidor && servidor !== atual;
    } catch {
        return false;
    } finally {
        if (timer) clearTimeout(timer);
    }
}
